import { Cell } from '@table-library/react-table-library/table';

import {
    evidenceFieldColumn as fieldColumn,
    evidenceValueColumn as valueColumn,
} from '../GscTools/columns';
import { formatCount } from './rows';

/**
 * The columns of the report's tables, built on the shared evidence columns: an absent value reads
 * "Not returned", a URL is a link, and a row's tone colours what Google returned.
 */

/**
 * A returned count. Not returned stays in words — in the export too — and sorts below a returned
 * zero, so "no count" never hides among the clean records. Only errors are coloured.
 */
function countColumn(t, { label, key, tone = '' }) {
    return {
        label,
        key,
        data: key,
        sortType: (array) => array.sort((a, b) => (a[key] ?? -1) - (b[key] ?? -1)),
        cellSize: 'minmax(110px, 0.7fr)',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (data === null || data === undefined) {
                return csvDownload ? (
                    t('tools.gsc.not_returned')
                ) : (
                    <span className="text-muted">{t('tools.gsc.not_returned')}</span>
                );
            }
            if (csvDownload) return data;

            return <span className={data > 0 ? tone : ''}>{formatCount(data)}</span>;
        },
    };
}

function rawColumn(label) {
    return {
        label,
        key: 'raw',
        data: 'raw',
        sortType: 'string',
        cellSize: 'minmax(200px, 1.4fr)',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data ?? '';
            if (data === null || data === undefined) return <span className="text-muted">—</span>;
            return <code className="gsc-tool-expression">{data}</code>;
        },
    };
}

/** Block 01, and an index's child records — one row per submitted sitemap. */
export const RECORD_COLUMNS = (t) => [
    valueColumn(t, {
        label: t('tools.sitemap_status.records.path'),
        key: 'path',
        isUrl: true,
        cellSize: 'minmax(280px, 2.6fr)',
    }),
    valueColumn(t, {
        label: t('tools.sitemap_status.records.type'),
        key: 'type',
        cellSize: 'minmax(130px, 0.9fr)',
    }),
    valueColumn(t, {
        label: t('tools.sitemap_status.records.last_submitted'),
        key: 'lastSubmitted',
        cellSize: 'minmax(150px, 1fr)',
    }),
    valueColumn(t, {
        label: t('tools.sitemap_status.records.last_downloaded'),
        key: 'lastDownloaded',
        cellSize: 'minmax(150px, 1fr)',
    }),
    valueColumn(t, {
        label: t('tools.sitemap_status.records.pending'),
        key: 'pending',
        cellSize: 'minmax(100px, 0.7fr)',
    }),
    countColumn(t, { label: t('tools.sitemap_status.records.warnings'), key: 'warnings' }),
    countColumn(t, {
        label: t('tools.sitemap_status.records.errors'),
        key: 'errors',
        tone: 'loss',
    }),
];

/** Block 02 — the selected record's fields, readable and as returned. */
export const DETAIL_COLUMNS = (t) => [
    fieldColumn(t('tools.sitemap_status.details.field')),
    valueColumn(t, {
        label: t('tools.sitemap_status.details.reading'),
        key: 'reading',
        cellSize: 'minmax(220px, 1.6fr)',
    }),
    rawColumn(t('tools.sitemap_status.details.raw')),
];

/** Block 03 — the selected record's submitted entries by content type. */
export const CONTENT_COLUMNS = (t) => [
    fieldColumn(t('tools.sitemap_status.contents.type'), { key: 'type' }),
    countColumn(t, { label: t('tools.sitemap_status.contents.submitted'), key: 'submitted' }),
    valueColumn(t, {
        label: t('tools.sitemap_status.contents.interpretation_column'),
        key: 'interpretation',
    }),
];

/** Block 04 — what the records flag, and the scope they are held under. */
export const ISSUE_COLUMNS = (t) => [
    fieldColumn(t('tools.sitemap_status.issues.scope'), {
        key: 'scope',
        cellSize: 'minmax(180px, 1fr)',
    }),
    valueColumn(t, { label: t('tools.sitemap_status.issues.evidence'), key: 'evidence' }),
];
